import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { NotificationBell } from "./NotificationBell";
import {
  ChartLineUp, SquaresFour, ListChecks, FolderOpen, CalendarCheck, FileText,
  Wallet, Robot, Ticket, UsersThree, SignOut, Buildings, VideoCamera, ShieldCheck, ChatCircle, ArrowUUpLeft, Eye, BookOpenText, List
} from "@phosphor-icons/react";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: SquaresFour, end: true },
  { to: "/projects", label: "Project Tracker", icon: ListChecks },
  { to: "/bookkeeping", label: "Bookkeeping", icon: BookOpenText },
  { to: "/documents", label: "Documents", icon: FolderOpen },
  { to: "/compliance", label: "Compliance", icon: CalendarCheck },
  { to: "/reports", label: "Reports", icon: FileText },
  { to: "/finance", label: "Invoices & Payments", icon: Wallet },
  { to: "/meetings", label: "Meetings", icon: VideoCamera },
  { to: "/messages", label: "Messages", icon: ChatCircle },
  { to: "/ai-cfo", label: "AI Virtual CFO", icon: Robot },
  { to: "/tickets", label: "Support Tickets", icon: Ticket },
  { to: "/team", label: "Team Directory", icon: UsersThree },
  { to: "/settings", label: "Security 2FA", icon: ShieldCheck },
];

export default function PortalLayout() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => { await logout(); navigate("/login"); };
  const [open, setOpen] = useState(false);
  const returnToAdmin = async () => {
    try {
      const { data } = await api.post("/auth/stop-impersonate");
      setUser(data);
      toast.success("Back in Admin console");
      navigate("/admin");
    } catch { toast.error("Could not return to admin"); }
  };

  return (
    <div className="min-h-screen flex">
      {user?.imp_by && (
        <div data-testid="impersonation-banner" className="fixed top-0 left-0 right-0 z-50 bg-[#FFD700] text-[#111] text-sm flex items-center justify-center gap-3 py-2 px-4">
          <Eye size={16} weight="fill" />
          <span>Viewing <b>{user.company}</b> as admin ({user.email})</span>
          <button data-testid="return-to-admin" onClick={returnToAdmin}
            className="inline-flex items-center gap-1 bg-[#111] text-white px-3 py-1 rounded-sm text-xs hover:bg-black transition-colors">
            <ArrowUUpLeft size={13} /> Return to Admin
          </button>
        </div>
      )}
      {open && <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={() => setOpen(false)} />}
      <aside className={`w-64 shrink-0 bg-[#111111] text-white flex-col fixed h-screen z-40 ${open ? "flex" : "hidden md:flex"} ${user?.imp_by ? "pt-10" : ""}`}>
        <div className="p-5 flex items-center gap-2 border-b border-white/10">
          <ChartLineUp size={24} weight="fill" color="#fff" />
          <span className="font-head font-black tracking-tight">VIRTUALEENA</span>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={`nav-${n.label.replace(/[\s&]+/g,'-').toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                  isActive ? "bg-[#002FA7] text-white" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
              <n.icon size={18} weight="regular" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-sm font-semibold truncate">{user?.name}</div>
          <div className="text-white/40 text-xs mb-3 truncate">{user?.company} · {user?.role}</div>
          <button data-testid="logout-btn" onClick={doLogout}
            className="flex items-center gap-2 text-sm text-white/60 hover:text-[#FF3B30] transition-colors">
            <SignOut size={18} /> Sign out
          </button>
        </div>
      </aside>

      <div className={`flex-1 md:ml-64 min-w-0 ${user?.imp_by ? "pt-10" : ""}`}>
        <header className="h-16 border-b border-black/10 bg-white/70 backdrop-blur-xl sticky top-0 z-20 flex items-center justify-between px-4 md:px-8">
          <div className="flex items-center gap-3">
            <button data-testid="portal-menu-btn" onClick={() => setOpen(true)} className="md:hidden p-1"><List size={22} /></button>
            <p className="label">{user?.company || "Client Portal"}</p>
          </div>
          <div className="flex items-center gap-4">
            <NotificationBell tone="dark" />
            <div className="w-9 h-9 rounded-sm bg-[#002FA7] text-white flex items-center justify-center font-head font-bold">
              {user?.name?.[0] || "U"}
            </div>
          </div>
        </header>
        <main className="p-4 md:p-8 max-w-[1400px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
